import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api/api.js";

import {
  ArrowLeft,
  Receipt,
  FileText,
  Package,
  ImageIcon,
  DollarSign,
  Calendar,
  Store,
  Upload,
} from "lucide-react";

export default function ReceiptDetailMobile() {
  const { id: receiptId } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("info");
  const [notes, setNotes] = useState("");

  const {
    data: receipt,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ["receipt", receiptId],
    queryFn: async () => {
      const response = await api.get(`/receipts/${receiptId}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 150;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveSection(sectionId);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("-");
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return `$${Number.parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 border border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "failed":
        return "bg-red-100 text-red-800 border border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const metrics = receipt
    ? {
        totalAmount: receipt.receipt_data?.total_amount || 0,
        lineItemsCount: receipt.receipt_data?.line_items?.length || 0,
        taxAmount: receipt.receipt_data?.tax_amount || 0,
        subtotal: receipt.receipt_data?.subtotal_amount || 0,
      }
    : { totalAmount: 0, lineItemsCount: 0, taxAmount: 0, subtotal: 0 };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      <span className="ml-3 text-gray-600">Loading...</span>
    </div>
  );

  const navigationItems = [
    { id: "info", label: "Info", icon: Receipt },
    { id: "lineitems", label: "Items", icon: Package },
    { id: "ReceiptImage", label: "Receipt Image", icon: ImageIcon },
    { id: "notes", label: "Notes", icon: FileText },
  ];

  return (
    <div className="bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="px-4 py-3">
          <button
            className="text-gray-600 hover:text-gray-900 mb-2 flex items-center gap-2 transition-colors"
            onClick={() => navigate("/receipts")}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Receipts
          </button>
          <h1 className="text-xl font-light text-gray-900">Receipt Details</h1>
        </div>
      </div>

      <div className="fixed top-[72px] left-0 right-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="overflow-x-auto">
          <div className="flex gap-2 px-4 py-3 min-w-max">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="px-4 py-6 pt-16 pb-32">
        {loading && <LoadingSpinner />}
        {error && (
          <div className="text-red-600 p-3 bg-red-50 rounded-lg border border-red-200 text-sm">
            {error.message || "Failed to load receipt details."}
          </div>
        )}

        {!loading && !error && receipt && (
          <>
            {/* Metrics cards - stacked on mobile */}
            <div className="space-y-4 mb-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm">Total Amount</span>
                </div>
                <div className="text-3xl font-light text-gray-900">
                  {formatCurrency(metrics.totalAmount)}
                </div>
                <div className="text-sm text-gray-500">
                  Subtotal: {formatCurrency(metrics.subtotal)}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Package className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm">Line Items</span>
                </div>
                <div className="text-3xl font-light text-gray-900">
                  {metrics.lineItemsCount}
                </div>
                <div className="text-sm text-gray-500">
                  Items on this receipt
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Receipt className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm">Tax Amount</span>
                </div>
                <div className="text-3xl font-light text-gray-900">
                  {formatCurrency(metrics.taxAmount)}
                </div>
                <div className="text-sm text-gray-500">Sales tax charged</div>
              </div>
            </div>

            {/* Receipt Info section */}
            <div
              id="info"
              className="bg-white rounded-lg border border-gray-200 p-4 mb-6 shadow-sm scroll-mt-32"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-gray-600" />
                  <h2 className="text-lg font-light text-gray-900">
                    Receipt Information
                  </h2>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                    receipt.processing_status
                  )}`}
                >
                  {receipt.processing_status}
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">
                    File Name
                  </label>
                  <div className="text-sm text-gray-900 font-medium">
                    {receipt.original_name}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-600 mb-1 block flex items-center gap-2">
                    <Store className="w-3 h-3" />
                    Merchant
                  </label>
                  <div className="text-sm text-gray-900 font-medium">
                    {receipt.receipt_data?.merchant_name || "-"}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-600 mb-1 block flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    Receipt Date
                  </label>
                  <div className="text-sm text-gray-900 font-medium">
                    {receipt.receipt_data?.date
                      ? formatDate(receipt.receipt_data.date)
                      : "-"}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-600 mb-1 block">
                    Uploaded
                  </label>
                  <div className="text-sm text-gray-900 font-medium">
                    {new Date(receipt.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">
                      File Size
                    </label>
                    <div className="text-sm text-gray-900 font-medium">
                      {(receipt.file_size / 1024).toFixed(2)} KB
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">
                      Type
                    </label>
                    <div className="text-sm text-gray-900 font-medium">
                      {receipt.content_type.split("/")[1].toUpperCase()}
                    </div>
                  </div>
                </div>
              </div>

              {receipt.processing_status === "pending" && (
                <div className="mt-4 bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                  <div className="flex items-center">
                    <div className="w-3 h-3 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                    <div>
                      <h3 className="text-xs font-semibold text-yellow-800">
                        Processing Receipt
                      </h3>
                      <p className="text-yellow-700 text-xs">
                        Data will appear once complete.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {receipt.processing_status === "failed" && (
                <div className="mt-4 bg-red-50 rounded-lg p-3 border border-red-200">
                  <h3 className="text-xs font-semibold text-red-800">
                    Processing Failed
                  </h3>
                  <p className="text-red-700 text-xs">
                    Unable to extract data from this receipt.
                  </p>
                </div>
              )}
            </div>

            {/* Line Items section */}
            <div
              id="lineitems"
              className="bg-white rounded-lg border border-gray-200 p-4 mb-6 shadow-sm scroll-mt-32"
            >
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-4 h-4 text-gray-600" />
                <h2 className="text-lg font-light text-gray-900">Line Items</h2>
              </div>

              {receipt.receipt_data?.line_items &&
              receipt.receipt_data.line_items.length > 0 ? (
                <div className="space-y-3">
                  {receipt.receipt_data.line_items.map((item, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-3 bg-white"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-sm text-gray-900 font-medium flex-1">
                          {item.name || "Unknown Item"}
                        </h3>
                        {item.category && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-md font-medium border border-gray-200 ml-2">
                            {item.category}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">Qty:</span>
                          <div className="text-gray-900 font-medium">
                            {item.quantity || 1}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Unit:</span>
                          <div className="text-gray-900 font-medium">
                            {formatCurrency(item.unit_price)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Total:</span>
                          <div className="text-gray-900 font-medium">
                            {formatCurrency(item.total_price)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No line items found.</p>
                </div>
              )}
            </div>

            {/* Documents section */}
            <div
              id="ReceiptImage"
              className="bg-white rounded-lg border border-gray-200 p-4 mb-6 shadow-sm scroll-mt-32"
            >
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon className="w-4 h-4 text-gray-600" />
                <h2 className="text-lg font-light text-gray-900">
                  Receipt Image
                </h2>
              </div>
              <p className="text-xs text-gray-600 mb-4">
                Original receipt file and related documents
              </p>
              <div className="space-y-3">
                {receipt.s3_url ? (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-900 truncate">
                            {receipt.original_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(receipt.file_size / 1024).toFixed(2)} KB
                          </div>
                        </div>
                      </div>
                    </div>
                    <a
                      href={receipt.s3_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center px-3 py-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-md transition-colors shadow-sm text-sm"
                    >
                      View File
                    </a>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      No file available
                    </p>
                  </div>
                )}
                <button className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors text-sm flex items-center justify-center gap-2">
                  <Upload className="w-4 h-4" />
                  Upload Document
                </button>
              </div>
            </div>

            {/* Notes section */}
            <div
              id="notes"
              className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm scroll-mt-32"
            >
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-gray-600" />
                <h2 className="text-lg font-light text-gray-900">
                  Receipt Notes
                </h2>
              </div>
              <textarea
                className="w-full h-32 p-3 border border-gray-300 rounded-md bg-white text-gray-900 shadow-sm text-sm"
                placeholder="Add notes about this receipt..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <div className="flex justify-end mt-3">
                <button className="px-4 py-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-md transition-colors shadow-sm text-sm">
                  Save Notes
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
